import { get, map, pick, sortBy } from 'lodash/fp'
import {
  IPaginationReq,
  IPaginationState,
  ThunkAction,
} from '../../types/common'
import { query } from '../../common/api'
import { IBranch, IDivision } from '../../types/user'
import { IInventoryEvent, IProduct, IProductTag } from '../../types/products'
import { ILocation } from '../../types/location'
import {
  BRANCH_BIN_LOCATION_ITEMS_QUERY,
  BRANCHES_PRODUCTS_LIST_QUERY,
  HISTORY_LOCATIONS_INTERNAL_QUERY,
  HISTORY_LOCATIONS_MANAGER_QUERY,
  INTERNAL_BRANCHES_PRODUCTS_LIST_QUERY,
  INTERNAL_PRODUCTS_QUERY,
  INTERNAL_SALABLE_PRODUCTS_LIST_QUERY,
  INVENTORY_EVENTS_INTERNAL_QUERY,
  INVENTORY_EVENTS_MANAGER_QUERY,
  PRODUCT_CREATE_QUERY,
  PRODUCT_INVENTORY_INTERNAL_QUERY,
  PRODUCT_INVENTORY_QUERY,
  PRODUCT_REMOVE_QUERY,
  PRODUCT_TAG_CREATE_QUERY,
  PRODUCT_TAG_DELETE_QUERY,
  PRODUCT_TAG_UPDATE_QUERY,
  PRODUCT_TRANSFER_QUERY,
  PRODUCT_UPDATE_QUERY,
  PRODUCTS_QUERY,
  SALABLE_PRODUCTS_LIST_QUERY,
} from './inventoryQueries'

const makeGetters = (isInternal: boolean, response: any) => ({
  fromBranch: (path: string) =>
    get(
      isInternal
        ? `internal.accounts.nodes[0].currentBranch[0].${path}`
        : `manager.currentBranch[0].${path}`,
      response,
    ),
  fromAccount: (path: string) =>
    get(
      isInternal
        ? `internal.accounts.nodes[0].${path}`
        : `manager.account.${path}`,
      response,
    ),
})

// New API nests product identity under `product`; flatten so components stay flat (IProduct).
const flattenInventoryProduct = (ip: any): IProduct =>
  ip && ip.product ? { ...ip, ...ip.product } : ip

export interface ISearchDivisions {
  [key: string]: IProduct[]
}

export interface IInventoryList {
  products: ISearchDivisions
  divisions: IDivision[]
  distributors: string[]
  hasInvoices: boolean
  productTags: IProductTag[]
  untaggedProductsCount: number
}

export interface ITagFilter {
  productTagId?: number
  untaggedOnly?: boolean
}

export const requestInventory =
  (
    branchId: number,
    accountId?: number,
    includeLocationItems = false,
    tagFilter?: ITagFilter,
  ): ThunkAction<Promise<IInventoryList>> =>
  async (dispatch) => {
    const isInternal = accountId != null
    const response = isInternal
      ? await query(INTERNAL_PRODUCTS_QUERY, {
          branchId,
          accountId,
          includeLocationItems,
          ...tagFilter,
        })
      : await query(PRODUCTS_QUERY, {
          branchId,
          includeLocationItems,
          ...tagFilter,
        })
    const { fromBranch, fromAccount } = makeGetters(isInternal, response)
    const products = ((fromBranch('inventoryProducts') as any[]) ?? []).map(
      flattenInventoryProduct,
    )
    const divisions = sortBy<IDivision>(
      'name',
      fromAccount('divisions'),
    ) as IDivision[]
    const distributors = fromAccount('distributorsList') as string[]
    const hasInvoices = fromAccount('hasInvoices') as boolean
    const productTags = (fromBranch('productTags') as IProductTag[]) ?? []
    const untaggedProductsCount =
      (fromBranch('untaggedProductsCount') as number) ?? 0

    const groups = divisions.reduce(
      (acct, division) => {
        const gr = products.filter((t) =>
          (t.divisions || [])
            .map((d: { id?: number }) => d.id)
            .includes(division.id),
        )
        return {
          ...acct,
          [`${division.name}-${division.id}`]: gr,
        }
      },
      {
        all: products,
        unallocated: products.filter((p) => !p.divisions.length),
        lowstock: products.filter(
          (p) =>
            p.notifyThreshold &&
            ((p.threshold && p.threshold >= Number(p.inventory)) ||
              (p.threshold === 0 && Number(p.inventory) === 0)),
        ),
        archived: [],
      },
    )

    return {
      products: groups,
      divisions,
      distributors,
      hasInvoices,
      productTags,
      untaggedProductsCount,
    }
  }

export const requestSalableProductsList =
  (
    branchId: number,
    code?: string,
    name?: string,
    accountId?: number,
  ): ThunkAction<Promise<IProduct[]>> =>
  async (dispatch) => {
    const isInternal = accountId != null
    const response = isInternal
      ? await query(INTERNAL_SALABLE_PRODUCTS_LIST_QUERY, {
          accountId,
          branchId,
          textCode: code,
          textName: name,
        })
      : await query(SALABLE_PRODUCTS_LIST_QUERY, {
          branchId,
          textCode: code,
          textName: name,
        })
    const { fromAccount } = makeGetters(isInternal, response)
    return fromAccount('salableCatalog') as IProduct[]
  }

export const requestBranchesProductsList =
  (
    branchId: number,
    code?: string,
    name?: string,
    accountId?: number,
  ): ThunkAction<Promise<IProduct[]>> =>
  async (dispatch) => {
    const isInternal = accountId != null
    const response = isInternal
      ? await query(INTERNAL_BRANCHES_PRODUCTS_LIST_QUERY, {
          accountId,
          branchId,
          textCode: code,
          textName: name,
        })
      : await query(BRANCHES_PRODUCTS_LIST_QUERY, {
          branchId,
          textCode: code,
          textName: name,
        })
    const { fromAccount } = makeGetters(isInternal, response)
    return fromAccount('branchesCatalog') as IProduct[]
  }

export interface IInventoryEventsResult {
  events: IInventoryEvent[]
  paginationState: IPaginationState
  inventory?: number
  updatedAt?: string
}

export const requestInventoryEvents =
  ({
    productId,
    branchId,
    accountId,
    eventTypes,
    locationId,
    throughEventId,
    pagination,
  }: {
    productId: number
    branchId: number
    accountId?: number
    eventTypes: Array<IInventoryEvent['eventType']>
    locationId: number | null
    // loads every event down to this one, ignoring first
    throughEventId: number | null
    pagination: IPaginationReq
  }): ThunkAction<Promise<IInventoryEventsResult>> =>
  async (dispatch) => {
    const isInternal = !!accountId
    const response = isInternal
      ? await query(INVENTORY_EVENTS_INTERNAL_QUERY, {
          branchId,
          accountId,
          productId,
          eventTypes,
          locationId,
          throughEventId,
          ...pagination,
        })
      : await query(INVENTORY_EVENTS_MANAGER_QUERY, {
          branchId,
          productId,
          eventTypes,
          locationId,
          throughEventId,
          ...pagination,
        })

    const { fromBranch } = makeGetters(isInternal, response)
    const productData = fromBranch('inventoryProducts[0]') as {
      inventoryEvents: { nodes: IInventoryEvent[]; pageInfo: IPaginationState }
      inventory: number
      updatedAt: string
    }

    return {
      events: productData?.inventoryEvents?.nodes ?? [],
      paginationState: productData?.inventoryEvents?.pageInfo ?? {
        endCursor: '',
        startCursor: '',
        hasNextPage: false,
        hasPreviousPage: false,
      },
      inventory: productData?.inventory,
      updatedAt: productData?.updatedAt,
    }
  }

export const requestHistoryLocations =
  ({
    productId,
    branchId,
    accountId,
  }: {
    productId: number
    branchId: number
    accountId?: number
  }): ThunkAction<Promise<Array<Pick<ILocation, 'id' | 'code'>>>> =>
  async () => {
    const isInternal = !!accountId
    const response = isInternal
      ? await query(HISTORY_LOCATIONS_INTERNAL_QUERY, {
          branchId,
          accountId,
          productId,
        })
      : await query(HISTORY_LOCATIONS_MANAGER_QUERY, { branchId, productId })

    const { fromBranch } = makeGetters(isInternal, response)
    return fromBranch('inventoryProducts[0].historyLocations') ?? []
  }

export const requestProductUpsert =
  (product: IProduct, branch: IBranch): ThunkAction<Promise<any>> =>
  async (dispatch, getState) => {
    const { id, divisions, code, ...productAttributes } = product

    const inventoryMissing = !product.inventory && product.inventory !== 0
    if (
      !product.unit ||
      !product.code ||
      !product.quantity ||
      !product.name ||
      (!product.price && product.price !== 0) ||
      (!product.custom && inventoryMissing)
    ) {
      const errors = {
        unit: !product.unit ? 'required' : '',
        code: !product.code ? 'required' : '',
        quantity: !product.quantity ? 'required' : '',
        name: !product.name ? 'required' : '',
        price: !product.price ? 'required' : '',
        inventory: !product.custom && inventoryMissing ? 'required' : '',
      }

      return { errors }
    }

    if (
      product.notifyThreshold &&
      !product.threshold &&
      product.threshold !== 0
    ) {
      return { threshold: 'required' }
    }

    const divisionIds = map('id', divisions)
    const params = {
      ...pick(
        [
          'distributor',
          'name',
          'quantity',
          'inventory',
          'threshold',
          'notifyThreshold',
          'unit',
          'truckLocationThreshold',
          'binLocationThreshold',
          'technicianThreshold',
          'locationItemsAttributes',
          'reason',
          'blobSignedId',
          'deletePhoto',
          'productTagIds',
        ],
        productAttributes,
      ),
      price: product.price && `${product.price}`,
      divisionIds,
      ...(productAttributes.pendingNewTag
        ? { newProductTag: productAttributes.pendingNewTag }
        : {}),
    }

    const response = id
      ? get(
          'inventoryProductUpdate',
          await query(PRODUCT_UPDATE_QUERY, {
            branchId: branch.id,
            productId: id,
            attributes: params,
            lockToken: product.lockToken,
          }),
        )
      : get(
          'inventoryProductCreate',
          await query(PRODUCT_CREATE_QUERY, {
            attributes: {
              productCode: code,
              ...params,
              branchId: branch.id,
            },
          }),
        )
    return { ...response, result: flattenInventoryProduct(response?.result) }
  }

export const requestProductRemove =
  (product: IProduct, branch: IBranch): ThunkAction<Promise<any>> =>
  async (dispatch, getState) => {
    const response = await query(PRODUCT_REMOVE_QUERY, {
      branchId: branch.id,
      productId: product.id,
    })
    return get('inventoryProductRemove.errors', response)
  }

export const requestBranchBinLocationItems =
  (branchId: number, productId: number): ThunkAction<Promise<any[]>> =>
  async () => {
    const response = await query(BRANCH_BIN_LOCATION_ITEMS_QUERY, {
      branchId,
      productId,
    })
    return get('branchBinLocationItems', response) ?? []
  }

export const requestProductCurrentInventory =
  (
    productId: number,
    branchId: number,
    accountId?: number,
  ): ThunkAction<
    Promise<Pick<IProduct, 'inventory' | 'updatedAt' | 'lockToken'> | undefined>
  > =>
  async () => {
    const isInternal = accountId != null
    const response = isInternal
      ? await query(PRODUCT_INVENTORY_INTERNAL_QUERY, {
          branchId,
          accountId,
          productId,
        })
      : await query(PRODUCT_INVENTORY_QUERY, { branchId, productId })

    const { fromBranch } = makeGetters(isInternal, response)
    const fresh = fromBranch('inventoryProducts[0]') as
      | Pick<IProduct, 'id' | 'inventory' | 'updatedAt' | 'lockToken'>
      | undefined
    // omit id: it is the InventoryProduct id, and patching it would overwrite the
    // list item's product id (used to query events / update the product)
    return fresh && pick(['inventory', 'updatedAt', 'lockToken'], fresh)
  }

export const requestTagCreate =
  (branchId: number, name: string, color: string): ThunkAction<Promise<any>> =>
  async () => {
    return get(
      'productTagCreate',
      await query(PRODUCT_TAG_CREATE_QUERY, { branchId, name, color }),
    )
  }

export const requestTagUpdate =
  (id: number, name?: string, color?: string): ThunkAction<Promise<any>> =>
  async () => {
    return (
      get(
        'productTagUpdate',
        await query(PRODUCT_TAG_UPDATE_QUERY, { id, name, color }),
      ) ?? {}
    )
  }

export const requestTagDelete =
  (id: number): ThunkAction<Promise<any>> =>
  async () => {
    return (
      get('productTagDelete', await query(PRODUCT_TAG_DELETE_QUERY, { id })) ??
      {}
    )
  }

export const requestProductTransfer =
  (
    productCode: string,
    sourceBranchId: number,
    destinationBranchId: number,
    sourceAmount: number,
    destinationAmount: number,
    sourceLocationId?: number,
    sourceLocationIds?: number[],
  ): ThunkAction<Promise<any>> =>
  async (dispatch, getState) => {
    if (
      !sourceAmount ||
      !destinationAmount ||
      !productCode ||
      !sourceBranchId ||
      !destinationBranchId
    ) {
      return { base: 'All fields required' }
    }

    if (sourceBranchId === destinationBranchId) {
      return { base: 'Target branch the same' }
    }

    return get(
      'InventoryProductTransfer',
      await query(PRODUCT_TRANSFER_QUERY, {
        sourceBranchId,
        destinationBranchId,
        productCode,
        attributes: {
          sourceAmount,
          destinationAmount,
          ...(sourceLocationId ? { sourceLocationId } : {}),
          ...(sourceLocationIds?.length ? { sourceLocationIds } : {}),
        },
      }),
    )
  }
